import type {
  BlockActionError,
  BlockActionErrorCode,
} from "./htmlBlockRuntime";

/**
 * Thrown by a host's action handler to answer the block with a code it can
 * show ("Not started — credits"). Any other rejection reaches the block as
 * `failed` with the error's message.
 */
export class BlockActionFailure extends Error {
  readonly code: BlockActionErrorCode;
  readonly retryAfterMs?: number;

  constructor(
    code: BlockActionErrorCode,
    message: string,
    retryAfterMs?: number,
  ) {
    super(message);
    this.name = "BlockActionFailure";
    this.code = code;
    this.retryAfterMs = retryAfterMs;
  }
}

/**
 * Runs one `cohesive.<name>(input)` call from a block, as the viewer.
 * Resolves with the value the block's promise receives.
 */
export type BlockActionHandler = (
  name: string,
  input: unknown,
) => Promise<unknown>;

export function toBlockActionError(err: unknown): BlockActionError {
  if (err instanceof BlockActionFailure) {
    return {
      code: err.code,
      message: err.message,
      ...(err.retryAfterMs !== undefined
        ? { retryAfterMs: err.retryAfterMs }
        : {}),
    };
  }
  return {
    code: "failed",
    message:
      err instanceof Error && err.message ? err.message : "The action failed.",
  };
}

/** How long after a forwarded click the host still takes an action from a
 *  resting block. The runtime keeps the same window on its side. */
export const BLOCK_ACTION_ACTIVATION_MS = 5000;
/** An upload follows a file picker, which can stay open for minutes. */
export const BLOCK_FILE_ACTION_ACTIVATION_MS = 10 * 60 * 1000;
